// Emergency Numbers → one domain, in a side sheet: verdict, plan, decision,
// approval, dry run / execute, and the raw records behind it all.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import DomainPlan from "./DomainPlan";
import DomainRecords from "./DomainRecords";
import { MODE, ModePill, PLATFORM_LABEL, Pill, StatusPill, ago } from "./ui";
import { PartnerWarningDialog } from "./warning/PartnerWarningDialog";

export default function DomainDrawer({ target, canEdit, onClose, onChanged, onShowRun }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState("");
  const [warn, setWarn] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const open = !!target;

  const load = useCallback(async () => {
    if (!target) return;
    try {
      const { data } = await api.get(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}`);
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the domain");
    }
  }, [target]);

  useEffect(() => { setDoc(null); setTranscript([]); load(); }, [load]);

  const reassess = useCallback(async () => {
    if (!target) return;
    setBusy("assess");
    try {
      const { data } = await api.post(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/assess`);
      setTranscript(data.transcript || []);
      setDoc((cur) => ({ ...(cur || {}), ...data, fix_runs: cur?.fix_runs || [] }));
      toast.success(`${target.domain} re-assessed: ${data.status}`
        + (data.numbers_refresh?.ok ? ` — Numbers tab updated (${data.numbers_refresh.numbers} number${data.numbers_refresh.numbers === 1 ? "" : "s"})` : ""));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-assessment failed");
    } finally {
      setBusy("");
    }
  }, [target, onChanged]);

  const fixUser = useCallback(async (userId, number, addressId) => {
    if (!target) return false;
    const isDefault = userId === "domain";
    try {
      await api.post(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/users/fix`,
        { user: userId, number, address_id: addressId });
      toast.success(isDefault ? `Domain default set on the Portal (${addressId}) — re-assessing ${target.domain}`
        : `Fixed ${userId}'s emergency caller-id on the Portal — re-assessing ${target.domain}`);
      await reassess();          // Iter 310 — also refreshes this domain's rows in the Numbers tab
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply the fix");
      return false;
    }
  }, [target, reassess]);

  const fixAllUsers = useCallback(async (userIds, number, addressId) => {
    if (!target || !userIds.length) return false;
    try {
      const { data } = await api.post(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/users/fix-all`,
        { users: userIds, number, address_id: addressId });
      if (data.fixed === data.total) toast.success(`Fixed all ${data.fixed} mismatched user(s) on the Portal`);
      else toast.warning(`Fixed ${data.fixed} of ${data.total} user(s) — some failed, see the row for details`);
      await reassess();          // Iter 310 — live re-check + Numbers snapshot refresh for this domain
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply the bulk fix");
      return false;
    }
  }, [target, reassess]);

  const deleteEndpoint = useCallback(async (tn) => {
    if (!target) return false;
    try {
      const { data } = await api.post(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/endpoints/delete`,
        { number: tn, delete_address: true });
      toast.success(`Removed endpoint ${tn}${data.address_id ? ` and address ${data.address_id}` : ""} from the Portal — re-assessing ${target.domain}`);
      await reassess();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the endpoint");
      return false;
    }
  }, [target, reassess]);

  const markIntended = useCallback(async (userIds, reason, intended = true) => {
    if (!target || !userIds.length) return false;
    try {
      const { data } = await api.post(`/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/mismatch/intended`,
        { users: userIds, reason, intended });
      toast.success(intended
        ? `${data.users.length} subscriber(s) marked intended — ${data.mismatch} still flagged on ${target.domain}`
        : `${data.users.length} subscriber(s) re-flagged`);
      await load();
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the mark");
      return false;
    }
  }, [target, load, onChanged]);

  const c = doc?.counts || {};
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl" data-testid="e911-domain-drawer">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2 font-heading" data-testid="e911-drawer-title">
            {target?.domain}
            {doc && <StatusPill status={doc.status} testid="e911-drawer-status" />}
            {doc && <ModePill mode={doc.expected_mode} testid="e911-drawer-mode" />}
            {doc?.has_siptrunk && <Pill tone="bg-indigo-50 text-indigo-700">trunk</Pill>}
            {doc?.sandbox && <Pill tone="bg-slate-200 text-slate-600">sandbox</Pill>}
          </SheetTitle>
          <SheetDescription asChild>
            <div className="text-[12px] text-slate-500">
              {doc ? (
                <>
                  {PLATFORM_LABEL[doc.platform] || doc.platform}{doc.territory ? ` · ${doc.territory}` : ""} ·{" "}
                  {doc.regular_users} regular user{doc.regular_users === 1 ? "" : "s"} ({doc.users_total} total) ·
                  assessed {ago(doc.assessed_at)}
                  {doc.expected_mode && MODE[doc.expected_mode] && <> · should be <strong>{MODE[doc.expected_mode].long}</strong></>}
                </>
              ) : "Loading…"}
            </div>
          </SheetDescription>
        </SheetHeader>

        {!doc ? (
          <div className="py-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-300" /></div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="grid grid-cols-5 gap-1 text-center" data-testid="e911-drawer-counts">
                {[["Addresses", c.addresses], ["Endpoints", c.endpoints], ["Legacy", c.legacy], ["User addr.", c.user_addresses],
                  ["Count API", c.api_total ?? "?"]].map(([l, v]) => (
                  <div key={l} className="rounded border border-slate-200 bg-white px-2 py-1">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{l}</div>
                    <div className="text-sm font-semibold text-slate-800">{v ?? 0}</div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={reassess} disabled={busy === "assess"} className="ml-auto"
                      data-testid="e911-drawer-reassess">
                {busy === "assess" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-assess live
              </Button>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setWarn(true)} data-testid="e911-drawer-warn-partner"
                        className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800">
                  <Megaphone className="h-4 w-4" /> Warn partner
                </Button>
              )}
            </div>
            <PartnerWarningDialog target={target} open={warn} onOpenChange={setWarn} />

            {doc.error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="e911-drawer-error">
                {doc.error}
              </div>
            )}

            <DomainPlan doc={doc} canEdit={canEdit} onChanged={async () => { await load(); onChanged?.(); }}
                        onReassess={reassess} onShowRun={onShowRun} />

            {!!transcript.length && (
              <details className="rounded-md border border-slate-200 bg-slate-50" data-testid="e911-drawer-transcript">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600">
                  Transcript of the live assessment ({transcript.length} lines)
                </summary>
                <pre className="max-h-72 overflow-auto px-3 pb-3 text-[11px] leading-relaxed text-slate-700">
                  {transcript.join("\n")}
                </pre>
              </details>
            )}

            {!!(doc.user_fixes || []).length && (
              <details className="rounded-md border border-slate-200 bg-slate-50" data-testid="e911-drawer-fix-history">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600">
                  Fix history ({doc.user_fixes.length})
                </summary>
                <div className="max-h-72 overflow-auto px-3 pb-3">
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        <th className="py-1 pr-2">When</th><th className="py-1 pr-2">User</th>
                        <th className="py-1 pr-2">Before</th><th className="py-1 pr-2">After</th>
                        <th className="py-1 pr-2">By</th><th className="py-1">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.user_fixes.map((f, i) => (
                        <tr key={i} className="border-t border-slate-200" data-testid={`e911-fix-history-row-${i}`}>
                          <td className="py-1 pr-2 whitespace-nowrap text-slate-500">{ago(f.at)}</td>
                          <td className="py-1 pr-2 font-mono">{f.user}</td>
                          <td className="py-1 pr-2 font-mono text-slate-500">{f.old_number || "—"} / {f.old_address_id || "—"}</td>
                          <td className="py-1 pr-2 font-mono text-slate-700">{f.number} / {f.address_id}</td>
                          <td className="py-1 pr-2 text-slate-500">{f.by}</td>
                          <td className="py-1">
                            {f.ok ? <Pill tone="bg-emerald-50 text-emerald-700">fixed</Pill>
                                  : <Pill tone="bg-rose-100 text-rose-800">{f.error || "failed"}</Pill>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <DomainRecords doc={doc} canEdit={canEdit} onFixUser={fixUser} onFixAllUsers={fixAllUsers} onMarkIntended={markIntended}
                           onDeleteEndpoint={deleteEndpoint} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
